import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  defineAsyncComponent,
  toRefs,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { watchDebounced } from '@vueuse/core'
import { prop, path, uniqBy, whereEq } from 'ramda'
import { catalogApi } from '@services/catalog.service'
import { geoApi } from '@/services/geo.service'
import { paymentCalculatorApi } from '@services/paymentCalculator.service'
import { commercialOffersApi } from '@/services/commercialOffers.service'
import { useLoadingState } from '@/packages/vue-loading-state'
import { disableAfterDate } from '@utils/helpers'
import { pluralization } from '@utils/intl-helpers'
import type { BaseResource } from '@/plugins/resource/resource'
import { useSubmit, nestedValidatorErrorsParser } from '@/composables/useSubmit'
import { useModalOpener } from '@/composables/useModalOpener'
import useField from '@/composables/useField'
import { useSelectsDynamicOptions } from '@/composables/useSelectsDynamicOptions'
import useFuzzySort from '@/composables/useFuzzySort'
import { commercialOfferTransformFrom } from '@/modules/cabinet/modules/CommercialOffer/transformers'
import { COMMERCIAL_OFFER_SOURCE_TYPE } from '@/const'


export default /*@__PURE__*/_defineComponent({
  __name: 'equipmentMonthlyPrice',
  props: {
    category: {},
    category_uuid: {},
    financingType: {},
    sendResource: {},
    previewResource: {},
    categories: {},
    financing_types: {}
  },
  emits: ["finish"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

const FinancingTypeInfo = defineAsyncComponent(
  () => import('@/modules/cabinet/modules/CommercialOffer/components/FinancingTypeInfo.vue')
)
const EquipmentGetAnOffer = defineAsyncComponent(
  () => import('@/components/forms/equipmentGetAnOffer.vue')
)

const CustomerContactDetails = defineAsyncComponent(
  () =>
    import(
      '@/modules/cabinet/modules/FinancingApplication/components/Create.vue'
    )
)

const props = __props

const emit = __emit

const isOpenedDatePicker = ref(false)
const { t } = useI18n()

const FinancingPeriodPlurals = {
  one: t('month'),
  few: t('months'),
  many: t('months'),
}
const pluralFinancingPeriodMin = computed(() =>
  pluralization(FinancingPeriodPlurals, currentConfig.value?.leasePeriodMin)
)
const pluralFinancingPeriodMax = computed(() =>
  pluralization(FinancingPeriodPlurals, currentConfig.value?.leasePeriodMax)
)

function finish() {
  emit('finish', { result: { id: result.value.id } })
}
const formRef = ref()

const labels = ref({
  title: t('First and last name'),
  category: t('Category'),
  brand: t('Brand'),
  brandModel: t('Model'),
  firstRegistration: t('First registration'),
  locationCountry: t('Object country'),
  location: t('Object location'),
  targetCountry: t('Delivery country'),
  target: t('Delivery location'),
  priceAmount: t('Price without VAT, €'),
  scheduleType: t('Type of schedule'),
  period: t('Financing period, months'),
  downPayment: t('Down payment, €'),
  agencyFee: t('Agency fee, €'),
})

const form = ref({
  category: { id: props.category, uuid: props.category_uuid },
  financingType: props.financingType,
  sourceType: COMMERCIAL_OFFER_SOURCE_TYPE.financing_service
})
const result = ref(null)

const defaultSliderOptions = {
  'tooltip-placement': 'bottom',
  contained: true,
  'dot-size': 16,
}

const countriesLocationFilters = computed(() => ({
  target_country_id: form.value.targetCountry?.id,
  category_id: form.value.category?.id,
  financing_type: props.financingType?.id,
}))

const {
  loading: locationCountriesLoading,
  list: locationCountriesList,
  load: locationCountriesLoad,
  debounceSearch: locationCountriesSearch,
  get: locationCountriesGet,
} = useSelectsDynamicOptions(paymentCalculatorApi.countriesLocationList, countriesLocationFilters)

const countriesTargetFilters = computed(() => ({
  location_country_id: form.value.locationCountry?.id,
  category_id: form.value.category?.id,
  financing_type: props.financingType?.id,
  is_root: true,
}))
const {
  loading: targetCountriesLoading,
  list: targetCountriesList,
  load: targetCountriesLoad,
  debounceSearch: targetCountriesSearch,
  get: targetCountriesGet,
} = useSelectsDynamicOptions(paymentCalculatorApi.countriesTargetList, countriesTargetFilters)

const categoriesFilters = computed(() => ({
  location_country_id: form.value.locationCountry?.id,
  target_country_id: form.value.targetCountry?.id,
  financing_type: form.value.financingType?.id,
  is_root: true,
}))
const {
  loading: categoriesLoading,
  list: categoriesList,
  load: categoriesLoad,
  search: categoriesSearch,
  get: categoriesGet,
} = useSelectsDynamicOptions(paymentCalculatorApi.categoriesList, categoriesFilters)

const isLoad = ref(false)
const loadingState = useLoadingState()

onMounted(() => {
  getCalculatorConfig({
    location_country_id: form.value.locationCountry?.id,
    target_country_id: form.value.targetCountry?.id,
    category_id: form.value.category?.id,
  })

  if (0 === Number(form.value.agencyFee)) {
    agencyFeePercent.value = 0
  } else {
    const result =
      (form.value.agencyFee /
        (form.value.priceAmount - form.value.downPayment)) *
      100
    agencyFeePercent.value = Number.isFinite(result)
      ? Math.round((result + Number.EPSILON) * 100) / 100 || 0
      : 0
  }
})

const calculatorConfig = ref(null)

const scheduleTypes = ref([])
const financingTypes = ref([])
const allCalculatorConfig = ref([])

const currentConfig = computed(() => {
  if (form.value.scheduleType && form.value.financingType) {
    return allCalculatorConfig.value.find((config) => {
      const { scheduleType, financingType } = config
      const findCurrent = whereEq({
        scheduleType: form.value?.scheduleType,
        financingType: form.value?.financingType,
      })
      return findCurrent(config)
    })?.config
  }
  return null
})

const downPaymentConfig = computed(() => {
  if (currentConfig.value) {
    return {
      min: Math.round(
        (form.value.priceAmount || 0) * currentConfig.value.downPaymentMin
      ),
      max: Math.round(
        (form.value.priceAmount || 0) * currentConfig.value.downPaymentMax
      ),
    }
  }
  return null
})

const agencyFeePercent = ref(0)

const agencyFeeConfig = computed(() => {
  if (currentConfig.value) {
    return {
      min: currentConfig.value.agencyFeeMin * 100,
      max: currentConfig.value.agencyFeeMax * 100,
    }
  }
  return {}
})
const agencyFeeConfigValue = computed(() => {
  if (currentConfig.value) {
    return {
      min: Math.round(
        ((form.value.priceAmount || 0) - form.value.downPayment) *
          currentConfig.value.agencyFeeMin
      ),
      max: Math.round(
        ((form.value.priceAmount || 0) - form.value.downPayment) *
          currentConfig.value.agencyFeeMax
      ),
    }
  }
  return {}
})
watch(
  () => agencyFeePercent.value,
  (newValue) => {
    const result =
      ((form.value.priceAmount - form.value.downPayment) * newValue) / 100
    form.value.agencyFee = Number.isFinite(result)
      ? Math.round((result + Number.EPSILON) * 100) / 100 || 0
      : 0
  }
)

watch(currentConfig, (newConfig) => {
    form.value.period = newConfig?.leasePeriodDefault
  
    form.value.downPayment =
      Math.round(form.value.priceAmount * newConfig?.downPaymentDefault) || 0
    agencyFeePercent.value = newConfig?.agencyFeeDefault * 100
    form.value.agencyFee = Math.round(
      (form.value.priceAmount - form.value.downPayment) *
        newConfig?.agencyFeeDefault
    )
})

watchDebounced(
  () => form.value.priceAmount,
  (newPrice) => {
      form.value.downPayment =
        Math.round(
          (newPrice || 0) * currentConfig.value?.downPaymentDefault || 1
        ) || 0
      agencyFeePercent.value = currentConfig.value?.agencyFeeDefault * 100
      form.value.agencyFee = Math.round(
        ((newPrice || 0) - form.value.downPayment) *
          currentConfig.value?.agencyFeeDefault
      )
  },
  { debounce: 500 }
)
watchDebounced(
  () => form.value.downPayment,
  (newValue) => {
      const result = currentConfig.value?.agencyFeeDefault * 100
      agencyFeePercent.value = Number.isFinite(result) ? result : 0
      form.value.agencyFee = Math.round(
        ((form.value.priceAmount || 0) - newValue) *
          currentConfig.value?.agencyFeeDefault
      )
  },
  { debounce: 500 }
)
watchDebounced(
  () => form.value.agencyFee,
  (newValue) => {
    if (0 === Number(newValue)) {
      agencyFeePercent.value = 0
    } else {
      const result =
        (newValue / (form.value.priceAmount - form.value.downPayment)) * 100
      agencyFeePercent.value = Number.isFinite(result) ? result : 0
    }
  },
  { debounce: 500 }
)

const financingInfo = ref({})

function getFinancingInfo() {
  loadingState
    .load(paymentCalculatorApi.financingInfo.execute())
    .then(({ item }) => {
      financingInfo.value = item
    })
}
getFinancingInfo()

function getCalculatorConfig(params) {
  loadingState
    .load(paymentCalculatorApi.configs.execute(params))
    .then(({ items }) => {
      allCalculatorConfig.value = items
      scheduleTypes.value = uniqBy(
        prop('id'),
        items.map((el) => el.scheduleType)
      )
      financingTypes.value = uniqBy(
        prop('id'),
        items.map((el) => el.financingType)
      )
      form.value.scheduleType = scheduleTypes.value[0] || null
      
      if (props.financingType) {
        form.value.financingType = financingTypes.value.find(el => {
          return el.id === props.financingType.id
        }) || financingTypes.value[0] || null
      } 
      if (props.financing_types && props.financing_types.length) {
        financingTypes.value = financingTypes.value.filter(el => {
          return props.financing_types.includes(el.id)
        })
        form.value.financingType = financingTypes.value[0] || null
      } else {
        form.value.financingType = financingTypes.value[0] || null
      }
    })
}

watch(
  [
    () => form.value.category?.id,
    () => form.value.locationCountry?.id,
    () => form.value.targetCountry?.id,
  ],
  () => {
    getCalculatorConfig({
      location_country_id: form.value.locationCountry?.id,
      target_country_id: form.value.targetCountry?.id,
      category_id: form.value.category?.id,
    })
  }
)

const currentResource = ref(props.previewResource)

// TODO if require reset result
// watch(form, () => {
//   if (result.value) {
//     currentResource.value = props.previewResource
//     result.value = null
//   }
// }, { deep: true })

const request = (sendData, control) => {
  isLoad.value = true
  return useSubmit(
    send(commercialOfferTransformFrom(form.value)),
    control,
    nestedValidatorErrorsParser
  )
}

const send = async (sendData) => {
  try {
    const {
      data: { item },
    } = await commercialOffersApi.getPriceMonthly.execute(
      {},
      {
        ...sendData,
      }
    )
    result.value = item
    return item
  } catch (e) {
    isLoad.value = false
    return Promise.reject(e)
  }
}

function openShareModal() {
  // const { open, close } = useModalOpener({
  //   component: ShareCommercialOffer,
  //   link: result.value.pdfUrl,
  //   title: result.value.title,
  //   onClose: () => {
  //     close()
  //     finish()
  //   },
  // })
  // open()
}

async function saveAndShare() {
  currentResource.value = props.sendResource
  await request(form.value, formRef.value)
  openShareModal()
}

function openCreateApplicationModal() {
  currentResource.value = props.sendResource
  const { open, patchOptions } = useModalOpener({
    component: CustomerContactDetails,
    request: request.bind(null, form.value, formRef),
    finishFn: finish,
    classes: ['modal--size_lg'],
  })
  open()
}

async function validateAndSendApplication() {
  formRef.value.validate().then(({ valid }: { valid: boolean }) => {
    openCreateApplicationModal()
  })
}

// watch(
//   () => form.value.financingType,
//   (newVal) => {
//     if (newVal && financingInfo.value[newVal?.id]?.price_description) {
//       form.value.priceAmount = ''
//     }
//   }
// )

// fuzzy sort filter

const {
  loading: brandsLoading,
  resultList: brandsList,
  search: brandsSearch,
  getPaginated: brandsGetMore,
} = useFuzzySort(
  catalogApi.brands.list)

const currentBrand = computed(() => {
  return form.value.brand
})
const {
  loading: brandModelsLoading,
  resultList: brandModelsList,
  search: brandModelsSearch,
  getPaginated: brandModelsGetMore,
} = useFuzzySort(
  catalogApi.brands.modelList,
  'brand_ids',
  currentBrand,
  path(['value', 'id'])
)
watch(() => form.value.brand, () => {
  form.value.brandModel = null
})

function clearForm() {
  formRef.value.resetForm()
  result.value = null
}

function openGetAnOfferModal() {
  const { open } = useModalOpener({
    name: 'EquipmentGetAnOffer',
    component: EquipmentGetAnOffer,
    initial: form.value,
    classes: ['modal--size_lg'],
  }, {}, clearForm)
  open()
}

const __returned__ = { FinancingTypeInfo, EquipmentGetAnOffer, CustomerContactDetails, props, emit, isOpenedDatePicker, t, FinancingPeriodPlurals, pluralFinancingPeriodMin, pluralFinancingPeriodMax, finish, formRef, labels, form, result, defaultSliderOptions, countriesLocationFilters, locationCountriesLoading, locationCountriesList, locationCountriesLoad, locationCountriesSearch, locationCountriesGet, countriesTargetFilters, targetCountriesLoading, targetCountriesList, targetCountriesLoad, targetCountriesSearch, targetCountriesGet, categoriesFilters, categoriesLoading, categoriesList, categoriesLoad, categoriesSearch, categoriesGet, isLoad, loadingState, calculatorConfig, scheduleTypes, financingTypes, allCalculatorConfig, currentConfig, downPaymentConfig, agencyFeePercent, agencyFeeConfig, agencyFeeConfigValue, financingInfo, getFinancingInfo, getCalculatorConfig, currentResource, request, send, openShareModal, saveAndShare, openCreateApplicationModal, validateAndSendApplication, brandsLoading, brandsList, brandsSearch, brandsGetMore, currentBrand, brandModelsLoading, brandModelsList, brandModelsSearch, brandModelsGetMore, clearForm, openGetAnOfferModal, computed, defineAsyncComponent, toRefs, onMounted, ref, watch, get useI18n() { return useI18n }, get watchDebounced() { return watchDebounced }, get prop() { return prop }, get path() { return path }, get uniqBy() { return uniqBy }, get whereEq() { return whereEq }, get catalogApi() { return catalogApi }, get geoApi() { return geoApi }, get paymentCalculatorApi() { return paymentCalculatorApi }, get commercialOffersApi() { return commercialOffersApi }, get useLoadingState() { return useLoadingState }, get disableAfterDate() { return disableAfterDate }, get pluralization() { return pluralization }, get useSubmit() { return useSubmit }, get nestedValidatorErrorsParser() { return nestedValidatorErrorsParser }, get useModalOpener() { return useModalOpener }, get useField() { return useField }, get useSelectsDynamicOptions() { return useSelectsDynamicOptions }, get useFuzzySort() { return useFuzzySort }, get commercialOfferTransformFrom() { return commercialOfferTransformFrom }, get COMMERCIAL_OFFER_SOURCE_TYPE() { return COMMERCIAL_OFFER_SOURCE_TYPE } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})